import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import {
  STATES, PRIORITY_STATES, VEHICLE_TYPES, PAYMENT_METHODS, ODOMETER_STATUSES,
  SALE_TYPES, PHOTO_CATEGORIES, INCLUDED_ITEM_OPTIONS, DEFAULT_CLAUSES,
} from "../lib/constants";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../components/ui/tooltip";
import SignaturePad from "../components/SignaturePad";
import IDScanner from "../components/IDScanner";
import ShareScreen from "../components/ShareScreen";
import SEO from "../components/SEO";
import {
  ChevronLeft, ChevronRight, Search, Upload, X, FileText, CheckCircle2, Lock,
  Loader2, MapPin, ShieldCheck, User, ShoppingCart, Share2, Send, HelpCircle,
} from "lucide-react";

const STEPS = [
  "Your role", "State", "Your info", "Vehicle", "Odometer", "Sale Terms",
  "Disclosures", "Photos", "State Guidance", "Review", "Pay & Sign", "Share",
];

function Field({ label, children, hint, tooltip, required = false }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2">
        <Label className="block text-sm font-semibold text-gray-900">
          {label}
          {required && <span className="text-[var(--csd-danger)] ml-0.5">*</span>}
        </Label>
        {tooltip && (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" aria-label="What is this?" className="text-[var(--csd-text-secondary)] hover:text-[var(--csd-primary)] transition-colors">
                  <HelpCircle size={14} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
                {tooltip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      {children}
      {hint && <p className="text-xs text-[var(--csd-text-secondary)] mt-1.5">{hint}</p>}
    </div>
  );
}

export default function Builder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [agreement, setAgreement] = useState(null);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [stateGuidance, setStateGuidance] = useState(null);
  const [shareUrl, setShareUrl] = useState(null);
  const [initiatorSig, setInitiatorSig] = useState(null);

  // ----- Initial load / create -----
  useEffect(() => {
    let active = true;
    const run = async () => {
      try {
        if (id && id !== "new") {
          const a = await api.getAgreement(id);
          if (!active) return;
          setAgreement(a);
          // Resume at appropriate step if user comes back.
          // IMPORTANT: when returning from Stripe Checkout the URL contains
          // ?session_id=... — even if the backend hasn't yet processed the
          // payment, we MUST jump straight to step 10 (Pay & Sign) so the
          // user sees the verifying-then-signing UI, not the wizard from
          // step 0. The polling effect below confirms the payment.
          const params = new URLSearchParams(window.location.search);
          const returningFromStripe =
            params.has("session_id") || params.get("payment") === "cancelled";
          if (a.status === "completed") setStep(11);
          else if (a.payment?.status === "paid" || returningFromStripe) setStep(10);
        } else {
          // If the user came here from the car-value calculator, pull the
          // prefilled vehicle data out of sessionStorage so they don't have
          // to re-type year/make/model/mileage.
          let prefill = null;
          try {
            const raw = sessionStorage.getItem("csd_calc_prefill");
            if (raw) {
              const parsed = JSON.parse(raw);
              // Only honor it within the last 30 minutes
              if (parsed?.ts && Date.now() - parsed.ts < 30 * 60 * 1000) {
                prefill = parsed;
              }
              sessionStorage.removeItem("csd_calc_prefill");
            }
          } catch {
            // ignore malformed prefill data
          }
          const createPayload = {};
          if (prefill?.state) createPayload.state = prefill.state;
          let a = await api.createAgreement(createPayload);
          if (prefill?.vehicle) {
            try {
              a = await api.updateAgreement(a.id, { vehicle: prefill.vehicle });
            } catch {
              // non-fatal — user can re-enter manually
            }
            try {
              if (typeof window !== "undefined" && typeof window.gtag === "function") {
                window.gtag("event", "bill_of_sale_started_from_calculator", {
                  state: prefill?.state || null,
                  make: prefill?.vehicle?.make || null,
                  model: prefill?.vehicle?.model || null,
                  year: prefill?.vehicle?.year || null,
                });
              }
            } catch {
              // analytics is best-effort
            }
          }
          if (!active) return;
          navigate(`/builder/${a.id}`, { replace: true });
          setAgreement(a);
        }
      } catch (e) {
        toast.error("Could not load agreement");
      }
    };
    run();
    return () => { active = false; };
  }, [id, navigate]);

  useEffect(() => {
    if (!agreement?.state) return;
    api.stateGuidance(agreement.state).then(setStateGuidance).catch(() => {});
  }, [agreement?.state]);

  // Auto-default odometer unit when reaching step 4
  useEffect(() => {
    if (step === 4 && agreement && !agreement.odometer?.unit) {
      setField("odometer", "unit", "miles");
    }
  }, [step, agreement?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track the furthest wizard step the user has reached. The backend keeps
  // a monotonic max — so going back to step 2 after touching step 7 doesn't
  // overwrite the funnel record. Fire-and-forget; doesn't block UX.
  useEffect(() => {
    if (!agreement?.id) return;
    const current = Number(agreement.furthest_step || 0);
    if (step <= current) return;
    api.updateAgreement(agreement.id, { furthest_step: step }).catch(() => {});
  }, [step, agreement?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = useCallback(async (patch) => {
    setAgreement((prev) => ({ ...prev, ...patch }));
    setSaving(true);
    try {
      const fresh = await api.updateAgreement(agreement.id, patch);
      setAgreement(fresh);
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  }, [agreement?.id]);

  // Functional setField: avoids races when typing fast across fields of same section.
  const setField = useCallback((section, key, value) => {
    setAgreement((prev) => {
      const nextSection = { ...(prev?.[section] || {}), [key]: value };
      const nextAgreement = { ...prev, [section]: nextSection };
      // Fire-and-forget persist
      if (prev?.id) {
        api.updateAgreement(prev.id, { [section]: nextSection }).catch(() => {});
      }
      return nextAgreement;
    });
  }, []);

  const progress = ((step + 1) / STEPS.length) * 100;

  // ----- VIN decode -----
  const decodeVin = async () => {
    const vin = (agreement?.vehicle?.vin || "").trim();
    if (!vin) return toast.error("Enter a VIN first");
    toast.loading("Decoding VIN…", { id: "vin" });
    try {
      const d = await api.decodeVin(vin);
      toast.dismiss("vin");
      if (d.error && !d.year) return toast.error(d.error || "Could not decode VIN");
      const next = {
        ...agreement.vehicle,
        year: d.year || agreement.vehicle?.year,
        make: d.make || agreement.vehicle?.make,
        model: d.model || agreement.vehicle?.model,
        trim: d.trim || agreement.vehicle?.trim,
        body_type: d.body_type || agreement.vehicle?.body_type,
        vehicle_type: d.vehicle_type || agreement.vehicle?.vehicle_type,
      };
      update({ vehicle: next });
      toast.success("VIN decoded");
    } catch (e) {
      toast.dismiss("vin");
      toast.error("VIN decode failed");
    }
  };

  // ----- ID scan -----
  const handleIdExtract = (role, extracted) => {
    const cur = agreement?.[role] || {};
    const merged = {
      ...cur,
      full_name: extracted.full_name || cur.full_name,
      address: extracted.address || cur.address,
      city: extracted.city || cur.city,
      state: extracted.state || cur.state,
      zip: extracted.zip || cur.zip,
      id_number_optional: extracted.id_number || cur.id_number_optional,
    };
    update({ [role]: merged });
    toast.success("Fields autofilled — please review");
  };

  // ----- File upload -----
  const uploadFile = async (file, category) => {
    try {
      toast.loading("Uploading…", { id: "up" });
      const att = await api.uploadAttachment(agreement.id, file, category, "");
      toast.dismiss("up");
      toast.success("Uploaded");
      setAgreement((p) => ({ ...p, attachments: [...(p.attachments || []), att] }));
    } catch (e) {
      toast.dismiss("up");
      toast.error("Upload failed");
    }
  };

  const removeAttachment = async (attId) => {
    await api.deleteAttachment(agreement.id, attId);
    setAgreement((p) => ({ ...p, attachments: (p.attachments || []).filter((a) => a.id !== attId) }));
  };

  // ----- Step validation with specific missing-field feedback -----
  const validation = useMemo(() => {
    if (!agreement) return { ok: false, missing: [] };
    const v = agreement.vehicle || {}, o = agreement.odometer || {}, sale = agreement.sale || {};
    const initiator = agreement.initiator_role;
    const me = (initiator && agreement[initiator]) || {};
    const need = (label, val) => (val && String(val).trim() ? null : label);
    let missing = [];
    switch (step) {
      case 0: missing = [need("your role", initiator)]; break;
      case 1: missing = [need("state", agreement.state)]; break;
      case 2:
        missing = [
          need("full name", me.full_name),
          need("address", me.address),
          need("city", me.city),
          need("state", me.state),
          need("ZIP", me.zip),
          need("email", me.email),
        ];
        break;
      case 3:
        missing = [
          need("year", v.year),
          need("make", v.make),
          need("model", v.model),
        ];
        break;
      case 4:
        missing = [
          need("odometer reading", o.reading),
          need("unit", o.unit),
          need("status", o.status),
        ];
        break;
      case 5:
        missing = [
          need("sale price", sale.sale_price),
          need("payment method", sale.payment_method),
          need("sale type", sale.sale_type),
        ];
        break;
      default: missing = [];
    }
    missing = missing.filter(Boolean);
    return { ok: missing.length === 0, missing };
  }, [agreement, step]);

  const canContinue = validation.ok;

  const handleContinue = async () => {
    if (!canContinue) {
      const missing = validation.missing.join(", ");
      return toast.error(`Please fill: ${missing}`);
    }
    if (step < STEPS.length - 1) setStep(step + 1);
  };

  // ----- Pay (Stripe Checkout) -----
  const [paying, setPaying] = useState(false);
  const [verifyingPayment, setVerifyingPayment] = useState(false);

  const handlePay = async () => {
    if (paying) return;
    setPaying(true);
    try {
      const { url } = await api.paymentCheckout(agreement.id);
      if (!url) throw new Error("No checkout URL returned");
      // Hand off to Stripe. The user returns to
      // /builder/{id}?session_id=cs_... which we handle below.
      window.location.href = url;
    } catch (e) {
      setPaying(false);
      const detail = e?.response?.data?.detail || e?.message || "Could not start payment";
      toast.error(detail);
    }
  };

  // Handle return from Stripe Checkout: poll status, then refresh agreement.
  useEffect(() => {
    if (!agreement?.id) return;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const cancelled = params.get("payment") === "cancelled";

    if (cancelled) {
      toast.info("Payment cancelled — you can try again any time.");
      window.history.replaceState({}, "", `/builder/${agreement.id}`);
      return;
    }
    if (!sessionId) return;
    if (agreement.payment?.status === "paid") {
      // Already paid — just clean the URL and continue.
      window.history.replaceState({}, "", `/builder/${agreement.id}`);
      return;
    }

    let cancelledPoll = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 6;
    const INTERVAL_MS = 2000;
    setVerifyingPayment(true);

    const poll = async () => {
      if (cancelledPoll) return;
      attempts += 1;
      try {
        const s = await api.paymentStatus(sessionId);
        if (s.payment_status === "paid") {
          toast.success("Payment received — your PDF is unlocked.");
          const fresh = await api.getAgreement(agreement.id);
          setAgreement(fresh);
          window.history.replaceState({}, "", `/builder/${agreement.id}`);
          setVerifyingPayment(false);
          return;
        }
        if (s.status === "expired") {
          toast.error("Payment session expired. Please try again.");
          window.history.replaceState({}, "", `/builder/${agreement.id}`);
          setVerifyingPayment(false);
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          toast.error("Still verifying your payment. Refresh in a moment if your PDF isn't unlocked.");
          setVerifyingPayment(false);
          return;
        }
        setTimeout(poll, INTERVAL_MS);
      } catch (err) {
        if (attempts >= MAX_ATTEMPTS) {
          toast.error("Could not verify payment. Please refresh the page.");
          setVerifyingPayment(false);
          return;
        }
        setTimeout(poll, INTERVAL_MS);
      }
    };
    poll();

    return () => {
      cancelledPoll = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agreement?.id]);

  // ----- Sign as initiator -----
  const signAsInitiator = async () => {
    const role = agreement.initiator_role;
    if (!role) return toast.error("Set your role first");
    if (!agreement.legal_acknowledged) return toast.error("Confirm the legal acknowledgment first");
    if (!initiatorSig) return toast.error("Please add a signature");
    try {
      const a = await api.sign(agreement.id, { role, ...initiatorSig });
      setAgreement(a);
      toast.success("Signed");
      // Generate share link (path) and build full URL using current browser origin
      const sh = await api.shareLink(agreement.id);
      const path = sh.path || sh.url || "";
      const fullUrl = path.startsWith("http") ? path : `${window.location.origin}${path}`;
      setShareUrl(fullUrl);
      setStep(11);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Signing failed");
    }
  };

  // Restore share URL when navigating back to share step
  useEffect(() => {
    if (step === 11 && !shareUrl && agreement?.share_tokens) {
      const other = agreement.initiator_role === "buyer" ? "seller" : "buyer";
      const tok = (agreement.share_tokens || {})[other];
      if (tok) {
        setShareUrl(`${window.location.origin}/sign/${agreement.id}/${other}?t=${tok}`);
      }
    }
  }, [step, agreement, shareUrl]);

  if (!agreement) {
    return (
      <Layout>
        <SEO title="Builder | CarSaleDoc" description="Build your bill of sale." path="/builder" noindex />
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <Loader2 className="animate-spin mx-auto mb-3 text-[var(--csd-primary)]" />
          <p className="text-sm text-[var(--csd-text-secondary)]">Loading your draft…</p>
        </div>
      </Layout>
    );
  }

  const v = agreement.vehicle || {}, o = agreement.odometer || {};
  const sale = agreement.sale || {}, delivery = agreement.delivery || {}, lien = agreement.lien || {};
  const clauses = agreement.clauses?.length ? agreement.clauses : DEFAULT_CLAUSES;
  const initiator = agreement.initiator_role;
  const otherRole = initiator === "buyer" ? "seller" : "buyer";
  const me = initiator ? (agreement[initiator] || {}) : {};
  const veh = `${v.year || ""} ${v.make || ""} ${v.model || ""}`.trim() || "the vehicle";
  const completed = agreement.status === "completed";
  const paid = agreement.payment?.status === "paid";
  const signedAsInitiator = !!(initiator && agreement[initiator]?.signature);

  return (
    <Layout hideFooter>
      <SEO title="Builder | CarSaleDoc" description="Build your bill of sale." path="/builder" noindex />
      <div className="max-w-3xl mx-auto px-4 py-6 sm:py-10">
        {/* Progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between text-xs mb-2">
            <span className="font-semibold text-[var(--csd-text-secondary)]" data-testid="wizard-step-label">
              Step {step + 1} of {STEPS.length} — <span className="text-[var(--csd-primary)]">{STEPS[step]}</span>
            </span>
            <span className="text-[var(--csd-text-secondary)]">{saving ? "Saving…" : "Saved"}</span>
          </div>
          <div className="csd-progress-track">
            <div className="csd-progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <div className="csd-card p-5 sm:p-8">
          {/* STEP 0: Role */}
          {step === 0 && (
            <div className="space-y-6">
              <div>
                <h2 className="font-heading text-2xl font-bold">Are you the seller or the buyer?</h2>
                <p className="text-sm text-[var(--csd-text-secondary)] mt-1">You'll fill in your own info first. The other party gets a secure link to fill theirs.</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { value: "seller", icon: User, title: "I'm the seller", desc: "You're selling the vehicle." },
                  { value: "buyer", icon: ShoppingCart, title: "I'm the buyer", desc: "You're buying the vehicle." },
                ].map((opt) => {
                  const selected = initiator === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      data-testid={`role-${opt.value}`}
                      onClick={() => update({ initiator_role: opt.value })}
                      className={`text-left p-5 rounded-2xl border-2 transition-all ${
                        selected
                          ? "border-[var(--csd-primary)] bg-[var(--csd-primary-light)]/40 shadow-md"
                          : "border-gray-200 hover:border-[var(--csd-primary)]/50 bg-white"
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-10 h-10 rounded-xl grid place-items-center ${selected ? "bg-[var(--csd-primary)] text-white" : "bg-gray-100 text-gray-700"}`}>
                          <opt.icon size={18} />
                        </div>
                        <div className="font-heading font-bold text-base">{opt.title}</div>
                      </div>
                      <div className="text-sm text-[var(--csd-text-secondary)]">{opt.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* STEP 1: State */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="font-heading text-2xl font-bold">Where is the sale happening?</h2>
                <p className="text-sm text-[var(--csd-text-secondary)] mt-1">We tailor the checklist and disclosures to your state.</p>
              </div>
              <Field label="State">
                <Select value={agreement.state || ""} onValueChange={(val) => update({ state: val })}>
                  <SelectTrigger data-testid="state-select" className="h-12"><SelectValue placeholder="Select a state" /></SelectTrigger>
                  <SelectContent>
                    <div className="text-xs uppercase tracking-widest font-semibold text-[var(--csd-text-secondary)] px-3 py-1">Priority states</div>
                    {PRIORITY_STATES.map((st) => <SelectItem key={st} value={st} data-testid={`state-opt-${st}`}>{st}</SelectItem>)}
                    <div className="text-xs uppercase tracking-widest font-semibold text-[var(--csd-text-secondary)] px-3 py-1 mt-2">All states</div>
                    {STATES.filter((x) => !PRIORITY_STATES.includes(x)).map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}
                    <SelectItem value="Other">Other state</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              {agreement.state === "Other" && (
                <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800">
                  We can generate a general vehicle bill of sale, but state-specific DMV forms and transfer requirements may vary.
                </div>
              )}
            </div>
          )}

          {/* STEP 2: Your info (initiator) */}
          {step === 2 && initiator && (
            <div className="space-y-5">
              <div>
                <h2 className="font-heading text-2xl font-bold">Your information ({initiator})</h2>
                <p className="text-sm text-[var(--csd-text-secondary)] mt-1">Scan your ID to autofill, or type it in. The {otherRole} fills theirs later via the share link.</p>
              </div>
              <IDScanner agreementId={agreement.id} role={initiator} scanFn={api.scanId} onExtract={(d) => handleIdExtract(initiator, d)} />
              <Field label="Full legal name" required><Input data-testid={`${initiator}-full-name`} value={me.full_name || ""} onChange={(e) => setField(initiator, "full_name", e.target.value)} placeholder="First Middle Last" /></Field>
              <Field label="Address" required tooltip="Your street address (line 1). Apartment/unit can be included or added on a second line on the printed PDF."><Input data-testid={`${initiator}-address`} value={me.address || ""} onChange={(e) => setField(initiator, "address", e.target.value)} placeholder="123 Main St" /></Field>
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label="City" required><Input data-testid={`${initiator}-city`} value={me.city || ""} onChange={(e) => setField(initiator, "city", e.target.value)} /></Field>
                <Field label="State" required tooltip="2-letter state code (e.g., CA, TX, NY)."><Input data-testid={`${initiator}-state`} value={me.state || ""} onChange={(e) => setField(initiator, "state", e.target.value)} placeholder="CA" maxLength={2} /></Field>
                <Field label="ZIP code" required><Input data-testid={`${initiator}-zip`} value={me.zip || ""} onChange={(e) => setField(initiator, "zip", e.target.value)} placeholder="92101" /></Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Phone" tooltip="Used for the contract record. Not shared with third parties."><Input data-testid={`${initiator}-phone`} value={me.phone || ""} onChange={(e) => setField(initiator, "phone", e.target.value)} placeholder="(555) 123-4567" /></Field>
                <Field label="Email" required tooltip="The signed PDF copy will be sent here once both parties complete the agreement."><Input data-testid={`${initiator}-email`} type="email" value={me.email || ""} onChange={(e) => setField(initiator, "email", e.target.value)} placeholder="name@example.com" /></Field>
              </div>
              <Field
                label={`${otherRole === "buyer" ? "Buyer" : "Seller"}'s email (optional)`}
                tooltip="If you know it, we'll automatically email the signing invitation after you pay and sign. You can also skip this and share the link manually via WhatsApp, SMS, etc."
              >
                <Input
                  data-testid="counter-email"
                  type="email"
                  value={agreement.counter_email || ""}
                  onChange={(e) => update({ counter_email: e.target.value })}
                  placeholder={`${otherRole}@example.com`}
                />
              </Field>
              <Field label="ID / license number (optional)" tooltip="Your driver's license, state ID, or passport number. Helpful for record-keeping but not required to complete the bill of sale."><Input value={me.id_number_optional || ""} onChange={(e) => setField(initiator, "id_number_optional", e.target.value)} /></Field>
              <label className="flex items-start gap-3 p-3 rounded-xl border border-gray-200 cursor-pointer">
                <Checkbox checked={!!me.confirmation} onCheckedChange={(v) => setField(initiator, "confirmation", !!v)} data-testid={`${initiator}-confirm`} />
                <span className="text-sm text-[var(--csd-text-secondary)]">
                  {initiator === "seller"
                    ? "I confirm I am the legal owner or authorized to sell this vehicle."
                    : "I confirm I have inspected the vehicle or had the opportunity to inspect it before purchase."}
                </span>
              </label>
            </div>
          )}

          {/* STEP 3: Vehicle */}
          {step === 3 && (
            <div className="space-y-5">
              <div>
                <h2 className="font-heading text-2xl font-bold">Vehicle details</h2>
                <p className="text-sm text-[var(--csd-text-secondary)] mt-1">Enter the VIN — we'll try to auto-fill year, make and model.</p>
              </div>
              <Field label="VIN" required tooltip="The 17-character Vehicle Identification Number. Found on the driver-side dashboard near the windshield, on the driver-side door jamb, on the title, or on the registration card." hint="Usually 17 characters.">
                <div className="flex gap-2">
                  <Input data-testid="vin-input" value={v.vin || ""} onChange={(e) => setField("vehicle", "vin", e.target.value.toUpperCase())} placeholder="1HGCM82633A123456" className="h-12 font-mono tracking-wider" />
                  <Button type="button" data-testid="vin-decode-btn" variant="outline" className="h-12 px-4" onClick={decodeVin}>
                    <Search size={16} className="mr-2" />Decode VIN
                  </Button>
                </div>
                {v.vin && v.vin.length !== 17 && (
                  <p className="text-xs text-amber-600 mt-1.5">VIN is usually 17 characters. You can still continue.</p>
                )}
              </Field>
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label="Year" required><Input data-testid="vehicle-year" value={v.year || ""} onChange={(e) => setField("vehicle", "year", e.target.value)} placeholder="e.g. 2018" /></Field>
                <Field label="Make" required tooltip="The manufacturer of the vehicle (e.g., Honda, Toyota, Ford)."><Input data-testid="vehicle-make" value={v.make || ""} onChange={(e) => setField("vehicle", "make", e.target.value)} placeholder="e.g. Honda" /></Field>
                <Field label="Model" required><Input data-testid="vehicle-model" value={v.model || ""} onChange={(e) => setField("vehicle", "model", e.target.value)} placeholder="e.g. Civic" /></Field>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label="Trim" tooltip="The trim level or specific version of the model (e.g., LX, EX, Sport, Limited)."><Input value={v.trim || ""} onChange={(e) => setField("vehicle", "trim", e.target.value)} placeholder="e.g. EX" /></Field>
                <Field label="Body type" tooltip="Sedan, SUV, Coupe, Hatchback, Pickup, Convertible, etc."><Input value={v.body_type || ""} onChange={(e) => setField("vehicle", "body_type", e.target.value)} placeholder="e.g. Sedan" /></Field>
                <Field label="Color"><Input value={v.color || ""} onChange={(e) => setField("vehicle", "color", e.target.value)} placeholder="e.g. Silver" /></Field>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label="License plate" tooltip="The current license plate number displayed on the vehicle."><Input value={v.plate_number || ""} onChange={(e) => setField("vehicle", "plate_number", e.target.value)} /></Field>
                <Field label="Title number (optional)" tooltip="The certificate of title number issued by the DMV. Found on the front of the paper title document. Leave blank if you don't have the title in hand."><Input value={v.title_number || ""} onChange={(e) => setField("vehicle", "title_number", e.target.value)} /></Field>
                <Field label="Current registration state" tooltip="The state where the vehicle is currently registered. Often the same as the sale state, but not always."><Input value={v.registration_state || ""} onChange={(e) => setField("vehicle", "registration_state", e.target.value)} /></Field>
              </div>
              <Field label="Vehicle type">
                <Select value={v.vehicle_type || ""} onValueChange={(val) => setField("vehicle", "vehicle_type", val)}>
                  <SelectTrigger className="h-12"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{VEHICLE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
          )}

          {/* STEP 4: Odometer */}
          {step === 4 && (
            <div className="space-y-5">
              <div>
                <h2 className="font-heading text-2xl font-bold">Odometer disclosure</h2>
                <p className="text-sm text-[var(--csd-text-secondary)] mt-1">Many states require odometer disclosure on vehicle transfers.</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Odometer reading" required tooltip="The mileage shown on the vehicle's odometer at the time of sale. Take a photo to be safe.">
                  <Input data-testid="odometer-reading" type="number" value={o.reading || ""} onChange={(e) => setField("odometer", "reading", e.target.value)} placeholder="e.g. 62450" />
                </Field>
                <Field label="Unit" required>
                  <Select value={o.unit || "miles"} onValueChange={(val) => setField("odometer", "unit", val)}>
                    <SelectTrigger data-testid="odometer-unit" className="h-12"><SelectValue placeholder="Miles" /></SelectTrigger>
                    <SelectContent><SelectItem value="miles">Miles</SelectItem><SelectItem value="kilometers">Kilometers</SelectItem></SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label="Odometer date" tooltip="The date you took the odometer reading. Usually the same as the sale date.">
                <Input type="date" value={o.date || ""} onChange={(e) => setField("odometer", "date", e.target.value)} />
              </Field>
              <Field label="Status" required tooltip="• Actual mileage — the reading is accurate. • Not actual — the odometer was rolled back or replaced. • Exceeds mechanical limits — the odometer cap (e.g., 99,999) was reached and rolled over. • Discrepancy / unknown — the reading may not reflect true mileage.">
                <Select value={o.status || ""} onValueChange={(val) => setField("odometer", "status", val)}>
                  <SelectTrigger data-testid="odometer-status" className="h-12"><SelectValue placeholder="Select status" /></SelectTrigger>
                  <SelectContent>{ODOMETER_STATUSES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <div className="rounded-xl bg-[var(--csd-primary-light)]/40 border border-[var(--csd-primary-light)] p-4 text-sm text-[var(--csd-primary)]">
                You may still need to complete official DMV title or odometer forms.
              </div>
            </div>
          )}

          {/* STEP 5: Sale terms */}
          {step === 5 && (
            <div className="space-y-5">
              <div>
                <h2 className="font-heading text-2xl font-bold">Sale terms</h2>
                <p className="text-sm text-[var(--csd-text-secondary)] mt-1">Price, payment and delivery details.</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Sale price (USD)" required tooltip="The total agreed sale price for the vehicle, in US dollars."><Input data-testid="sale-price" type="number" value={sale.sale_price || ""} onChange={(e) => setField("sale", "sale_price", e.target.value)} placeholder="12500" /></Field>
                <Field label="Payment method" required tooltip="How the buyer is paying: cash, cashier's check, bank transfer, etc.">
                  <Select value={sale.payment_method || ""} onValueChange={(val) => setField("sale", "payment_method", val)}>
                    <SelectTrigger data-testid="payment-method" className="h-12"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label="Deposit paid?">
                  <Select value={sale.deposit_paid ? "yes" : "no"} onValueChange={(val) => setField("sale", "deposit_paid", val === "yes")}>
                    <SelectTrigger className="h-12"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="no">No</SelectItem><SelectItem value="yes">Yes</SelectItem></SelectContent>
                  </Select>
                </Field>
                <Field label="Deposit amount"><Input type="number" value={sale.deposit_amount || ""} onChange={(e) => setField("sale", "deposit_amount", e.target.value)} /></Field>
                <Field label="Balance due"><Input type="number" value={sale.balance_due || ""} onChange={(e) => setField("sale", "balance_due", e.target.value)} /></Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Payment due date"><Input type="date" value={sale.payment_due_date || ""} onChange={(e) => setField("sale", "payment_due_date", e.target.value)} /></Field>
                <Field label="Sale date"><Input type="date" value={delivery.sale_date || ""} onChange={(e) => setField("delivery", "sale_date", e.target.value)} /></Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Delivery date"><Input type="date" value={delivery.delivery_date || ""} onChange={(e) => setField("delivery", "delivery_date", e.target.value)} /></Field>
                <Field label="Delivery location"><Input value={delivery.delivery_location || ""} onChange={(e) => setField("delivery", "delivery_location", e.target.value)} /></Field>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label="County"><Input value={delivery.county || ""} onChange={(e) => setField("delivery", "county", e.target.value)} /></Field>
                <Field label="City"><Input value={delivery.city || ""} onChange={(e) => setField("delivery", "city", e.target.value)} /></Field>
                <Field label="State"><Input value={delivery.state || ""} onChange={(e) => setField("delivery", "state", e.target.value)} /></Field>
              </div>
              <Field label="Sale type" required tooltip="• As-is, no warranty — most common for private sales; buyer accepts vehicle in current condition. • With limited written warranty — seller offers a specific warranty written below. • Gift — no money exchanged; for tax / DMV purposes. • Trade — vehicle exchanged for another item. • Other — describe in writing.">
                <Select value={sale.sale_type || ""} onValueChange={(val) => setField("sale", "sale_type", val)}>
                  <SelectTrigger data-testid="sale-type" className="h-12"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{SALE_TYPES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              {sale.sale_type === "With limited written warranty" && (
                <Field label="Warranty terms"><Textarea value={sale.warranty_terms || ""} onChange={(e) => setField("sale", "warranty_terms", e.target.value)} /></Field>
              )}
              {sale.sale_type === "Gift" && (
                <Field label="Gift value / stated value"><Input value={sale.gift_value || ""} onChange={(e) => setField("sale", "gift_value", e.target.value)} /></Field>
              )}

              <div className="border-t border-gray-200 pt-5">
                <div className="flex items-center gap-1.5 mb-3">
                  <h3 className="font-heading font-bold text-lg">Liens or loans</h3>
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button type="button" aria-label="What is a lien?" className="text-[var(--csd-text-secondary)] hover:text-[var(--csd-primary)] transition-colors">
                          <HelpCircle size={14} />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
                        A lien is a legal claim by a lender (bank, credit union, finance company) on the vehicle until the loan is paid off. If you owe money on the vehicle, there is likely a lien. Check your title or registration.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <RadioGroup value={lien.status || "No, vehicle is free of liens"} onValueChange={(val) => update({ lien: { ...lien, status: val } })} className="space-y-2">
                  {["No, vehicle is free of liens", "Yes, lien exists", "Unknown"].map((opt) => (
                    <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer">
                      <RadioGroupItem value={opt} /> {opt}
                    </label>
                  ))}
                </RadioGroup>
                {lien.status === "Yes, lien exists" && (
                  <div className="grid sm:grid-cols-2 gap-4 mt-4">
                    <Field label="Lienholder name"><Input value={lien.lienholder_name || ""} onChange={(e) => update({ lien: { ...lien, lienholder_name: e.target.value } })} /></Field>
                    <Field label="Payoff responsibility">
                      <Select value={lien.payoff_responsibility || ""} onValueChange={(val) => update({ lien: { ...lien, payoff_responsibility: val } })}>
                        <SelectTrigger className="h-12"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Seller will pay off lien before transfer">Seller pays off before transfer</SelectItem>
                          <SelectItem value="Buyer is aware of lien">Buyer is aware of lien</SelectItem>
                          <SelectItem value="Other terms">Other terms</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STEP 6: Disclosures */}
          {step === 6 && (
            <div className="space-y-5">
              <div>
                <h2 className="font-heading text-2xl font-bold">Condition & disclosures</h2>
                <p className="text-sm text-[var(--csd-text-secondary)] mt-1">Select clauses that apply to your sale.</p>
              </div>
              <div className="space-y-3">
                {clauses.map((c) => (
                  <label key={c.key} className="flex items-start gap-3 p-3 rounded-xl border border-gray-200 hover:bg-gray-50 cursor-pointer">
                    <Checkbox
                      checked={!!c.selected}
                      onCheckedChange={(val) => {
                        const next = clauses.map((x) => x.key === c.key ? { ...x, selected: !!val } : x);
                        update({ clauses: next });
                      }}
                      data-testid={`clause-${c.key}`}
                    />
                    <span className="text-sm">{c.label}</span>
                  </label>
                ))}
              </div>
              <Field label="Known defects / issues">
                <Textarea data-testid="known-defects" value={agreement.known_defects || ""} onChange={(e) => update({ known_defects: e.target.value })} placeholder="Describe any known defects or issues…" />
              </Field>
              <Field label="Included items">
                <div className="flex flex-wrap gap-2">
                  {INCLUDED_ITEM_OPTIONS.map((item) => {
                    const checked = (agreement.included_items || []).includes(item);
                    return (
                      <button
                        type="button"
                        key={item}
                        onClick={() => {
                          const cur = agreement.included_items || [];
                          const next = checked ? cur.filter((x) => x !== item) : [...cur, item];
                          update({ included_items: next });
                        }}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${checked ? "bg-[var(--csd-primary)] text-white border-[var(--csd-primary)]" : "bg-white text-gray-700 border-gray-200 hover:border-[var(--csd-primary)]"}`}
                      >
                        {item}
                      </button>
                    );
                  })}
                </div>
              </Field>
            </div>
          )}

          {/* STEP 7: Photos */}
          {step === 7 && (
            <div className="space-y-5">
              <div>
                <h2 className="font-heading text-2xl font-bold">Photo attachments</h2>
                <p className="text-sm text-[var(--csd-text-secondary)] mt-1">Photos appear as labeled exhibits in the final PDF.</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {PHOTO_CATEGORIES.map((cat) => (
                  <label key={cat.value} className="border-2 border-dashed border-gray-300 rounded-xl p-5 text-center bg-gray-50 hover:bg-gray-100 cursor-pointer transition flex flex-col items-center gap-2">
                    <Upload size={18} className="text-[var(--csd-primary)]" />
                    <span className="text-sm font-semibold">{cat.label}</span>
                    <span className="text-xs text-[var(--csd-text-secondary)]">PNG, JPG up to 8MB</span>
                    <input type="file" accept="image/*" className="hidden" data-testid={`upload-${cat.value}`} onChange={(e) => { if (e.target.files?.[0]) uploadFile(e.target.files[0], cat.value); }} />
                  </label>
                ))}
              </div>
              {(agreement.attachments || []).length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-4">
                  {agreement.attachments.map((a) => (
                    <div key={a.id} className="relative group" data-testid={`attachment-${a.id}`}>
                      <img src={api.fileUrl(a.storage_path)} alt={a.category} className="w-full h-24 object-cover rounded-lg border border-gray-200" />
                      <button type="button" onClick={() => removeAttachment(a.id)} className="absolute top-1 right-1 bg-white/90 rounded-full p-1 opacity-0 group-hover:opacity-100">
                        <X size={14} />
                      </button>
                      <div className="text-xs mt-1 text-[var(--csd-text-secondary)]">{a.category}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* STEP 8: State guidance */}
          {step === 8 && (
            <div className="space-y-5">
              <div className="flex items-center gap-2">
                <MapPin className="text-[var(--csd-primary)]" />
                <h2 className="font-heading text-2xl font-bold">{agreement.state || "State"} guidance</h2>
              </div>
              {stateGuidance && (
                <>
                  <p className="text-sm text-[var(--csd-text-secondary)] leading-relaxed">{stateGuidance.intro}</p>
                  <ul className="space-y-2">
                    {stateGuidance.checklist.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 size={16} className="text-[var(--csd-primary)] mt-0.5 shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="rounded-xl bg-gray-50 border border-gray-200 p-4 text-xs text-[var(--csd-text-secondary)]">
                    This checklist is informational only and may not include all state, county or DMV requirements.
                  </div>
                </>
              )}
            </div>
          )}

          {/* STEP 9: Review */}
          {step === 9 && (
            <div className="space-y-5">
              <div>
                <h2 className="font-heading text-2xl font-bold">Review your contract</h2>
                <p className="text-sm text-[var(--csd-text-secondary)] mt-1">Preview the PDF before paying & signing.</p>
              </div>
              <div className="csd-card overflow-hidden">
                <iframe title="preview" src={api.pdfUrl(agreement.id)} className="w-full" style={{ height: "60vh" }} data-testid="review-pdf-iframe" />
              </div>
              <div className="flex flex-wrap gap-3">
                <Button variant="outline" onClick={() => setStep(0)} data-testid="review-edit">Edit details</Button>
                <a href={api.pdfUrl(agreement.id)} target="_blank" rel="noreferrer" data-testid="review-download-watermark">
                  <Button variant="outline"><FileText size={16} className="mr-2" />Open preview</Button>
                </a>
              </div>
            </div>
          )}

          {/* STEP 10: Pay & Sign */}
          {step === 10 && (
            <div className="space-y-6">
              <div>
                <h2 className="font-heading text-2xl font-bold">Pay & sign</h2>
                <p className="text-sm text-[var(--csd-text-secondary)] mt-1">{paid ? `Payment confirmed — sign as the ${initiator} below, then share the link with the ${otherRole}.` : `Unlock signing with a one-time payment, then sign first as the ${initiator} and share a secure link with the ${otherRole}.`}</p>
              </div>

              {!paid ? (
                <div className="csd-card p-6 bg-gradient-to-br from-white to-[var(--csd-primary-light)]/40">
                  <div className="flex items-start gap-3">
                    <ShieldCheck className="text-[var(--csd-primary)]" />
                    <div className="flex-1">
                      <h3 className="font-heading font-bold text-lg">How signing works — please read</h3>
                      <ol className="text-sm text-[var(--csd-text-secondary)] mt-3 space-y-2 list-decimal list-inside">
                        <li><span className="text-[var(--csd-text-primary)] font-medium">You pay $4.99 now</span> to unlock signing — one-time payment, both parties pay nothing else.</li>
                        <li><span className="text-[var(--csd-text-primary)] font-medium">You sign first</span> as the {initiator} on the next screen.</li>
                        <li><span className="text-[var(--csd-text-primary)] font-medium">You send a secure link to the {otherRole}</span>. They fill their info and sign — no account needed.</li>
                        <li><span className="text-[var(--csd-text-primary)] font-medium">Both parties receive the final signed PDF by email</span> as soon as the {otherRole} signs.</li>
                      </ol>
                      <div className="mt-5 pt-4 border-t border-[var(--csd-border)]/60">
                        <div className="flex items-baseline justify-between mb-3">
                          <span className="text-sm text-[var(--csd-text-secondary)]">One-time price</span>
                          <span className="font-heading font-bold text-2xl text-[var(--csd-text-primary)]">$4.99</span>
                        </div>
                        {verifyingPayment ? (
                          <div className="flex items-center gap-2 text-sm text-[var(--csd-text-secondary)]" data-testid="pay-verifying">
                            <Loader2 size={16} className="animate-spin text-[var(--csd-primary)]" />
                            Verifying your payment with Stripe… you'll be taken to signing in a moment.
                          </div>
                        ) : (
                          <Button
                            onClick={handlePay}
                            disabled={paying}
                            className="csd-btn-primary rounded-xl w-full sm:w-auto"
                            data-testid="pay-btn"
                          >
                            {paying ? (
                              <>
                                <Loader2 size={14} className="mr-2 animate-spin" />
                                Redirecting to Stripe…
                              </>
                            ) : (
                              <>
                                <Lock size={14} className="mr-2" />
                                Pay $4.99 and continue to sign
                              </>
                            )}
                          </Button>
                        )}
                        <p className="text-xs text-[var(--csd-text-secondary)] mt-3 inline-flex items-center gap-1">
                          <ShieldCheck size={12} />
                          Secure checkout hosted by Stripe — your card details never touch our servers.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <label className="flex items-start gap-3 p-4 rounded-xl border border-gray-200 cursor-pointer">
                    <Checkbox checked={!!agreement.legal_acknowledged} onCheckedChange={(v) => update({ legal_acknowledged: !!v })} data-testid="legal-ack" />
                    <span className="text-sm text-[var(--csd-text-secondary)]">
                      I understand that CarSaleDoc is not a law firm and this document does not replace official DMV forms or legal advice.
                    </span>
                  </label>

                  <div className="csd-card p-5">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-heading font-bold">Sign as {initiator} — {me.full_name || "—"}</h3>
                      {signedAsInitiator && <CheckCircle2 className="text-[var(--csd-primary)]" />}
                    </div>
                    {signedAsInitiator ? (
                      <div className="text-sm text-[var(--csd-text-secondary)]">
                        Signed {new Date(agreement[initiator].signature.signed_at).toLocaleString()}. The agreement is now waiting for the {otherRole}.
                        <div className="mt-4">
                          <Button onClick={() => setStep(11)} className="csd-btn-primary rounded-xl" data-testid="go-to-share">
                            <Share2 size={14} className="mr-2" />Continue to share
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <SignaturePad role={initiator} onChange={setInitiatorSig} />
                        <Button className="csd-btn-primary w-full mt-4 rounded-xl" data-testid="sign-initiator-btn" onClick={signAsInitiator}>
                          Sign and continue
                        </Button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* STEP 11: Share */}
          {step === 11 && (
            <div className="space-y-5">
              <div>
                <h2 className="font-heading text-2xl font-bold">Send to the {otherRole}</h2>
                <p className="text-sm text-[var(--csd-text-secondary)] mt-1">Share this secure link. They fill their info, sign, and both parties receive the final PDF.</p>
              </div>
              {shareUrl ? (
                <ShareScreen
                  url={shareUrl}
                  vehicleLabel={veh}
                  otherRoleLabel={otherRole}
                  agreementId={agreement.id}
                  prefillEmail={agreement.counter_email || ""}
                  alreadySentTo={(agreement.counter_invitations || []).slice(-1)[0]?.email || null}
                  onSent={(email) =>
                    setAgreement((a) => ({
                      ...a,
                      counter_email: email,
                      counter_invitations: [
                        ...(a?.counter_invitations || []),
                        { email, sent_at: new Date().toISOString(), status: "sent" },
                      ],
                    }))
                  }
                />
              ) : (
                <div className="text-sm text-[var(--csd-text-secondary)]">Generating share link…</div>
              )}

              {completed && (
                <div className="csd-card p-6 bg-[var(--csd-primary-light)]/40 border-[var(--csd-primary-light)]">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="text-[var(--csd-primary)] mt-0.5" />
                    <div>
                      <h3 className="font-heading font-bold text-lg">Completed and locked</h3>
                      <p className="text-sm text-[var(--csd-text-secondary)] mt-1">Both parties signed. A PDF copy was emailed to buyer and seller.</p>
                      <a href={api.pdfUrl(agreement.id)} target="_blank" rel="noreferrer" data-testid="download-final-pdf">
                        <Button className="csd-btn-primary mt-3 rounded-xl"><FileText size={14} className="mr-2" />Download final PDF</Button>
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Validation hint above navigation */}
        {!canContinue && step < 10 && validation.missing.length > 0 && (
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2" data-testid="validation-banner">
            <span className="font-semibold">Missing:</span>
            <span>{validation.missing.join(", ")}</span>
          </div>
        )}

        {/* Wizard navigation */}
        <div className="sticky bottom-0 mt-6 bg-[var(--csd-bg)] py-3 flex items-center justify-between gap-3">
          <Button variant="outline" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} data-testid="wizard-back">
            <ChevronLeft size={16} className="mr-1" />Back
          </Button>
          {step < 10 && (
            <Button className="csd-btn-primary rounded-xl flex-1 sm:flex-initial sm:px-8" onClick={handleContinue} data-testid="wizard-continue">
              Continue <ChevronRight size={16} className="ml-1" />
            </Button>
          )}
        </div>
      </div>
    </Layout>
  );
}
